import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { Box, Button, Card, CardContent, Chip, Paper, Typography } from "@material-ui/core";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import PageTitle from "../../components/PageTitle/PageTitle";
import SubscriptionService from "./Locality/Service/subscriptionService";

function getOrderIdFromLocation(routeSearch) {
  const fromRoute = new URLSearchParams(routeSearch || "").get("orderId");
  if (fromRoute) return fromRoute;
  const fromWindow = new URLSearchParams(window.location.search || "").get("orderId");
  if (fromWindow) return fromWindow;
  const hash = window.location.hash || "";
  const q = hash.indexOf("?");
  if (q === -1) return "";
  return new URLSearchParams(hash.slice(q + 1)).get("orderId") || "";
}

export default function PaymentFailed() {
  const routerLocation = useLocation();
  const userDetails = useMemo(() => {
    try {
      return JSON.parse(localStorage.getItem("userDetail") || "{}");
    } catch {
      return {};
    }
  }, []);

  const isSchool = String(userDetails?.role || "").toUpperCase() === "SCHOOLE";
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [payment, setPayment] = useState(null);

  const paymentOrderId = useMemo(
    () => getOrderIdFromLocation(routerLocation.search),
    [routerLocation.search, routerLocation.pathname],
  );

  useEffect(() => {
    if (!isSchool) return;
    if (!paymentOrderId) {
      setError("OrderId missing.");
      return;
    }
    setError("");
    setLoading(true);
    SubscriptionService.getReceipt(paymentOrderId)
      .then((res) => {
        const data = res?.data || res;
        setPayment(data?.payment || null);
      })
      .catch((e) => {
        setError(e?.response?.data?.message || "Unable to fetch payment status.");
      })
      .finally(() => setLoading(false));
  }, [isSchool, paymentOrderId]);

  if (!isSchool) {
    return (
      <Box p={3}>
        <Typography>You don&apos;t have access to this page.</Typography>
      </Box>
    );
  }

  const status = String(payment?.status || "").toUpperCase();
  const isSuccess = status === "SUCCESS";

  return (
    <Box p={3}>
      <PageTitle title="Payment Status" />

      {error ? (
        <Paper style={{ padding: 10, background: "#fff7ed", border: "1px solid #fed7aa", marginTop: 12 }}>
          <Typography style={{ color: "#9a3412", fontWeight: 700 }}>{error}</Typography>
        </Paper>
      ) : null}

      <Card style={{ maxWidth: 900, marginTop: 16, borderRadius: 16, boxShadow: "0 4px 24px rgba(15, 59, 98, 0.08)" }}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" style={{ gap: 12 }}>
            <Box display="flex" alignItems="center" style={{ gap: 12 }}>
              {isSuccess ? (
                <CheckCircleIcon style={{ color: "#16a34a" }} />
              ) : (
                <ErrorOutlineIcon style={{ color: "#dc2626" }} />
              )}
              <Typography style={{ fontWeight: 900, fontSize: "1.15rem", color: "#0f3b62" }}>
                {loading ? "Loading..." : isSuccess ? "Payment Successful" : "Payment Failed"}
              </Typography>
            </Box>
            <Chip
              label={status || (loading ? "LOADING" : "UNKNOWN")}
              style={{
                fontWeight: 800,
                color: "#fff",
                background: isSuccess ? "#22c55e" : "#ef4444",
              }}
            />
          </Box>

          <Box style={{ height: 1, background: "#eef2f6", margin: "16px 0" }} />

          <Typography variant="body2" style={{ color: "#334155", fontWeight: 700 }}>
            Order ID: <span style={{ fontWeight: 900, color: "#0f2744" }}>{payment?.orderId || "—"}</span>
          </Typography>
          <Typography variant="body2" style={{ color: "#334155", marginTop: 6 }}>
            Receipt No: <b>{payment?.receiptNumber || "—"}</b>
          </Typography>
          <Typography variant="body2" style={{ color: "#334155", marginTop: 6 }}>
            Tracking ID: <b>{payment?.trackingId || "—"}</b>
          </Typography>
          <Typography variant="body2" style={{ color: "#334155", marginTop: 6 }}>
            Bank Ref No: <b>{payment?.bankRefNo || "—"}</b>
          </Typography>

          {isSuccess ? null : (
            <Typography variant="body2" style={{ color: "#64748b", marginTop: 14 }}>
              If your amount was debited, please wait for some time and check again, or contact support with your Order ID.
            </Typography>
          )}

          <Box display="flex" alignItems="center" style={{ gap: 12, marginTop: 18, flexWrap: "wrap" }}>
            <Button
              variant="contained"
              color="primary"
              style={{ textTransform: "none", fontWeight: 800 }}
              onClick={() => {
                window.location.href = "/#/app/upgrade-plan";
              }}
            >
              Back to Upgrade Plan
            </Button>
            <Button
              variant="outlined"
              style={{ textTransform: "none", fontWeight: 800 }}
              onClick={() => window.location.reload()}
            >
              Refresh status
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

